import { all, call, put, takeEvery, take } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import { jt, t } from 'ttag'
import { getWhoAmI, getWhoAmIFail, getWhoAmISuccess } from '../../../common/auth/authActions'
import { addNotification, clearNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import { currentLangString, saveLocale } from '../../../i18nInit'
import api from '../../../services/api.services'
import {
  doLogin,
  setLoginInProgress,
  checkLoginMethod,
  checkLoginMethodSuccess,
  checkLoginMethodFail,
  completeSsoLogin,
} from './LoginActions'
import { isNative } from '../../../common/helpers/isNative'
import { reLocate } from '../../../common/Routes/RouterActions'
import { dataSync } from '../../../services/DataSync'
import { getErrorMsg } from '../../../common/helpers/translations'
import { Link as RouterLink } from 'react-router-dom'
import { ROUTES } from '../../../common/Routes/routePaths'
import Link from '@material-ui/core/Link'
import * as React from 'react'
import { LoginMethod } from '../../../services/api.types'
import { setVerifyEmailContext } from '../../VerifyEmail/store/VerifyEmailActions'

/**
 * Saga: email-first login method check.
 * Calls checkLoginMethod RPC with the user's email.
 * On success, dispatches the result to the reducer.
 * If SSO_REQUIRED, the component will handle the redirect.
 */
function* sagaCheckLoginMethod({ payload }: any) {
  try {
    yield put({ type: String(clearNotification) })
    const { email } = payload
    const response: any = yield call(api.auth.checkLoginMethod, { email })

    // Dispatch the result so the Login component can render the right step
    yield put({
      type: String(checkLoginMethodSuccess),
      payload: {
        loginMethod: response.loginMethod,
        companyId: response.companyId || null,
        companyName: response.companyName || null,
        ssoConfigId: response.ssoConfigId || null,
      },
    })
  } catch (e: any) {
    yield put({ type: String(checkLoginMethodFail) })

    // Rate limiting (429) is already handled globally in ApiClient interceptor.
    // Show a generic error for other failures.
    const errorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('checkLoginMethod'),
    }
    yield put({ type: String(addNotification), payload: errorNotification })
  }
}

/**
 * Saga: traditional email+password login.
 * Unchanged from original, with added ssoRequired error handling.
 */
function* sagaPasswordAuth({ payload }: any) {
  try {
    yield put({ type: String(clearNotification) })
    yield put({ type: String(setLoginInProgress), payload: true })
    const { email, password } = payload
    const { lang, requiresEmailVerification } = yield call(api.auth.login, {
      email,
      password,
      supportsEmailVerification: true,
      lang: currentLangString,
    })

    // If the user's company requires email verification, the back-end has already emailed a
    // 6-digit code and flagged the session as email-verification-in-progress. Sync the auth
    // state first (so the router knows the session is in the "mid-verification" state) before
    // navigating to the verify screen. Purpose is stored in Redux (not in router state) so
    // concurrent reLocate() redirects that push to /verify-email without state don't clobber it.
    if (requiresEmailVerification) {
      yield put(setVerifyEmailContext({ purpose: 'LOGIN', email }))
      yield put({ type: String(getWhoAmI) })
      yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
      yield put({ type: String(setLoginInProgress), payload: false })
      yield put(push(ROUTES.VERIFY_EMAIL.to))
      return
    }

    // Ensure that you're logged in now.
    yield put({ type: String(getWhoAmI) })

    // Just wait for getWhoAmI to finish. No need for extra call.
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])

    saveLocale(currentLangString)

    if (String(lang) !== currentLangString) {
      if (isNative()) {
        window.location.reload()
      } else {
        // Refresh the page and remove lang query parameter (if present).
        window.location.href = window.location.href.split('?')[0]
      }
    }

    yield put({ type: String(reLocate) })

    // Make sure Apple Health is synced on login.
    if (isNative()) {
      setTimeout(() => {
        dataSync(true).then()
      }, 1000)
    }
  } catch (e: any) {
    // Handle ssoRequired error: backend blocks password login when SSO is required
    if (e.message && e.message === 'ssoRequired') {
      const ssoNotification: INotification = {
        type: 'warning',
        content: t`This account requires SSO login. Please use the SSO sign-in option.`,
      }
      yield put({ type: String(addNotification), payload: ssoNotification })
    } else if (e.message && e.message === 'domainNotVerified') {
      // Domain verification required: company's email domain is not verified for SSO
      const notification: INotification = {
        type: 'warning',
        content: t`Your organization's domain has not been verified. Please contact your administrator.`,
      }
      yield put({ type: String(addNotification), payload: notification })
    } else if (e.status === 401 || e.status === 403) {
      let content = jt`Your email or password is incorrect`
      const msg = e.message.split('|')
      if (msg[0] === 'tooManyFailedAttempts') {
        const userFailedLoginLimit = parseInt(msg[1])
        const x = userFailedLoginLimit > 0 ? userFailedLoginLimit : 5
        const y = <Link component={RouterLink} to={ROUTES.FORGOT_PASSWORD.to}>{t`reset your password`}</Link>
        content = jt`There have been more than ${x} failed login attempts for this account. It is temporarily blocked. Try again later or ${y}.`
      }
      const loginErrorNotification: INotification = {
        type: 'warning',
        content,
      }
      yield put({ type: String(addNotification), payload: loginErrorNotification })
    } else {
      const loginErrorNotification: INotification = {
        type: 'warning',
        content: getErrorMsg('login'),
      }
      yield put({ type: String(addNotification), payload: loginErrorNotification })
    }
    yield put({ type: String(setLoginInProgress), payload: false })
  }
}

/**
 * Saga: SSO callback login.
 * Exchanges the sessionId with the backend, waits for getWhoAmI to finish,
 * then redirects to the correct view via reLocate.
 */
function* sagaCompleteSsoLogin({ payload }: any) {
  try {
    const { sessionId } = payload

    // Exchange sessionId with the backend
    yield call(api.auth.ssoCallback, { sessionId })

    // Load user data and wait for it to finish
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])

    // Redirect to the correct view
    yield put({ type: String(reLocate) })
  } catch (e: any) {
    console.error('SSO callback error:', e)
    // Redirect to /login with the error notification passed via location state
    // (RouterSaga clears notifications on LOCATION_CHANGE, then re-adds from state)
    let notification: INotification
    if (e.message && e.message === 'domainNotVerified') {
      notification = {
        type: 'warning',
        content: t`Your organization's domain has not been verified. Please contact your administrator.`,
      }
    } else {
      notification = {
        type: 'warning',
        content: t`SSO login failed. Please try again.`,
      }
    }
    yield put(push(ROUTES.LOGIN.to, { notification }))
  }
}

export function* loginSagas() {
  yield all([
    takeEvery(String(doLogin), sagaPasswordAuth),
    takeEvery(String(checkLoginMethod), sagaCheckLoginMethod),
    takeEvery(String(completeSsoLogin), sagaCompleteSsoLogin),
  ])
}
